import { AdminPageHeader } from "@admin-ui/components/composed/AdminPageHeader";
import { DeleteEntityDialog } from "@admin-ui/components/composed/DeleteEntityDialog";
import { AdminLayout } from "@admin-ui/components/frames/AdminLayout";
import { ConfirmationDialog } from "@admin-ui/components/primitives/application/confirmation-dialog";
import { Tab, TabList, TabPanel, Tabs } from "@admin-ui/components/primitives/application/tabs/tabs";
import { Button } from "@admin-ui/components/primitives/base/buttons/button";
import { Dropdown } from "@admin-ui/components/primitives/base/dropdown/dropdown";
import { Head, router, useForm, usePage } from "@inertiajs/react";
import { useModalStack } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import {
  adminAccountBadgePath,
  adminAccountBadgeRecomputationPath,
  adminAccountBadgesPath,
  adminAccountMemberPath,
  adminAccountPath,
  adminAccountsPath,
  adminUserPath,
  editAdminAccountMemberPath,
  newAdminAccountMemberPath,
  profilePath,
} from "@travily/routes";
import type { AdminAccountsAccountMember } from "@travily/types/models";
import type AdminAccountsShow from "@travily/types/pages/AdminAccountsShow";
import { Award02, Trash01 } from "@untitledui/icons";
import { type ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useAdminLayoutAccount } from "@/hooks/admin/use-admin-layout-account";
import { useAdminTabs } from "@/hooks/admin/use-admin-tabs";
import { useImageUploadField } from "@/hooks/use-image-upload-field";
import AdminPersistentLayout from "@/layouts/admin-persistent-layout";
import type { SharedProps } from "@/types";
import { BadgesAwardModal } from "./components/BadgesAwardModal";
import { BadgesPanel, type BadgesPanelCopy } from "./components/BadgesPanel";
import { InvitationPanel } from "./components/invitation-panel";
import {
  AccountMeta,
  MembersTabHeader,
  ProfileForm,
  type ProfileFormData,
  SellingForm,
  type SellingFormData,
  ShowView,
} from "./show.view";

type AdminAccountsShowPageProps = AdminAccountsShow;

interface ProfileSubmissionData extends ProfileFormData {
  avatar_signed_id: string;
  cover_image_signed_id: string;
  remove_avatar: boolean;
  remove_cover_image: boolean;
}

const VALID_TABS = new Set(["profile", "selling", "invitation", "members", "badges"]);
const TAB_ALIASES: Record<string, string> = { details: "profile" };

function AdminAccountsShowPage({
  account,
  memberships,
  invitation,
  supported_countries,
  badge_awards,
  available_badges,
}: AdminAccountsShowPageProps) {
  const navAccount = useAdminLayoutAccount();
  const { visitModal } = useModalStack();
  const { current_user } = usePage<SharedProps>().props;
  const [deleteOpen, setDeleteOpen] = useState(false);

  // An admin can't delete an account they belong to (the server guard rejects
  // it too); hide the affordance rather than show an action that will error.
  const isCurrentMember = memberships.some((member) => member.user_id != null && member.user_id === current_user?.id);
  // Anonymized accounts are terminal tombstones — re-deleting is rejected, so
  // hide the affordance.
  const isAnonymized = account.lifecycle_state === "anonymized";

  const { initialTab, onSelectionChange: onTabChange } = useAdminTabs({
    basePath: adminAccountPath(account.id),
    validTabs: VALID_TABS,
    defaultTab: "profile",
    aliases: TAB_ALIASES,
  });

  const profileForm = useForm<ProfileSubmissionData>({
    name: account.display_name ?? "",
    handle: account.handle ?? "",
    visibility: (account.visibility ?? "hidden") as ProfileFormData["visibility"],
    featured_on: account.featured_on ?? "",
    feature_url: account.feature_url ?? "",
    avatar_signed_id: "",
    cover_image_signed_id: "",
    remove_avatar: false,
    remove_cover_image: false,
  });
  const profileFormRef = useRef(profileForm);
  profileFormRef.current = profileForm;

  const sellingForm = useForm<SellingFormData>({
    country: account.country ?? "",
  });
  const sellingFormRef = useRef(sellingForm);
  sellingFormRef.current = sellingForm;

  const avatarField = useImageUploadField({
    initialPreviewUrl: account.avatar?.src ?? null,
    onUploaded: (signedId) =>
      profileFormRef.current.setData((prev) => ({ ...prev, avatar_signed_id: signedId, remove_avatar: false })),
    onRemoved: () => profileFormRef.current.setData((prev) => ({ ...prev, avatar_signed_id: "", remove_avatar: true })),
  });
  const coverField = useImageUploadField({
    initialPreviewUrl: account.cover_image_url,
    onUploaded: (signedId) =>
      profileFormRef.current.setData((prev) => ({
        ...prev,
        cover_image_signed_id: signedId,
        remove_cover_image: false,
      })),
    onRemoved: () =>
      profileFormRef.current.setData((prev) => ({ ...prev, cover_image_signed_id: "", remove_cover_image: true })),
  });

  const handleProfileSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      profileFormRef.current.transform((formData) => ({ account: formData }));
      profileFormRef.current.patch(adminAccountPath(account.id), { preserveScroll: "errors" });
    },
    [account.id],
  );

  const handleSellingSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      sellingFormRef.current.transform((formData) => ({ account: formData }));
      sellingFormRef.current.patch(adminAccountPath(account.id), { preserveScroll: "errors" });
    },
    [account.id],
  );

  const handleNameChange = useCallback((value: string) => {
    profileFormRef.current.setData("name", value);
    profileFormRef.current.clearErrors("name");
  }, []);

  const handleHandleChange = useCallback((value: string) => {
    profileFormRef.current.setData("handle", value);
    profileFormRef.current.clearErrors("handle");
  }, []);

  const handleVisibilityChange = useCallback((value: ProfileFormData["visibility"]) => {
    profileFormRef.current.setData("visibility", value);
  }, []);

  const handleFeaturedOnChange = useCallback((value: string) => {
    profileFormRef.current.setData("featured_on", value);
    profileFormRef.current.clearErrors("featured_on");
  }, []);

  const handleFeatureUrlChange = useCallback((value: string) => {
    profileFormRef.current.setData("feature_url", value);
    profileFormRef.current.clearErrors("feature_url");
  }, []);

  const handleCountryChange = useCallback((value: string) => {
    sellingFormRef.current.setData("country", value);
    sellingFormRef.current.clearErrors("country");
  }, []);

  const { onFileSelect: onAvatarFileSelect } = avatarField;
  const handleAvatarDrop = useCallback(
    (files: FileList) => {
      const file = files[0];
      if (file) onAvatarFileSelect(file);
    },
    [onAvatarFileSelect],
  );

  const { onFileSelect: onCoverFileSelect } = coverField;
  const handleCoverDrop = useCallback(
    (files: FileList) => {
      const file = files[0];
      if (file) onCoverFileSelect(file);
    },
    [onCoverFileSelect],
  );

  const handleRemoveMember = (membershipId: number) => {
    router.delete(adminAccountMemberPath(account.id, membershipId), {
      preserveScroll: true,
    });
  };

  const [revokeAwardId, setRevokeAwardId] = useState<number | null>(null);
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const revokeAward = useMemo(
    () => badge_awards.find((award) => award.id === revokeAwardId) ?? null,
    [badge_awards, revokeAwardId],
  );
  const handleGrantBadge = useCallback(
    ({ badgeKey, level, awardedOn }: { badgeKey: string; level: number; awardedOn?: string }) => {
      router.post(
        adminAccountBadgesPath(account.id),
        { badge_key: badgeKey, level, awarded_on: awardedOn },
        { preserveScroll: true },
      );
    },
    [account.id],
  );
  const handleRecomputeBadges = useCallback(() => {
    router.post(adminAccountBadgeRecomputationPath(account.id), {}, { preserveScroll: true });
  }, [account.id]);
  const handleRevokeBadgeRequest = useCallback((awardId: number) => {
    setRevokeAwardId(awardId);
  }, []);
  const handleRevokeBadgeConfirm = useCallback(() => {
    if (revokeAwardId === null) return;
    router.delete(adminAccountBadgePath(account.id, revokeAwardId), {
      preserveScroll: true,
      onFinish: () => setRevokeAwardId(null),
    });
  }, [account.id, revokeAwardId]);

  const badgesPanelCopy: BadgesPanelCopy = useMemo(
    () => ({
      title: t("admin.accounts.badges.title"),
      description: t("admin.accounts.badges.description"),
      awardAction: t("admin.accounts.badges.actions.award"),
      revokeAction: t("admin.accounts.badges.actions.revoke"),
      emptyTitle: t("admin.accounts.badges.empty.title"),
      emptyDescription: t("admin.accounts.badges.empty.description"),
      stats: {
        total: t("admin.accounts.badges.stats.total"),
        travel: t("admin.accounts.badges.stats.travel"),
        platform: t("admin.accounts.badges.stats.platform"),
        adminGrants: t("admin.accounts.badges.stats.admin_grants"),
      },
      filters: {
        all: t("admin.accounts.badges.filters.all"),
        travel: t("admin.accounts.badges.filters.travel"),
        platform: t("admin.accounts.badges.filters.platform"),
        admin: t("admin.accounts.badges.filters.admin_grants"),
        system: t("admin.accounts.badges.filters.system_grants"),
      },
      source: {
        system: t("admin.accounts.badges.source.system"),
        admin: t("admin.accounts.badges.source.admin"),
      },
      columns: {
        badge: t("admin.accounts.badges.columns.badge"),
        level: t("admin.accounts.badges.columns.level"),
        source: t("admin.accounts.badges.columns.source"),
        awarded: t("admin.accounts.badges.columns.awarded"),
        by: t("admin.accounts.badges.columns.by"),
      },
    }),
    [],
  );

  const awardModalCopy = useMemo(
    () => ({
      title: t("admin.accounts.badges.modal.title"),
      description: t("admin.accounts.badges.modal.description"),
      searchLabel: t("admin.accounts.badges.modal.search_label"),
      searchPlaceholder: t("admin.accounts.badges.modal.search_placeholder"),
      levelLabel: t("admin.accounts.badges.modal.level_label"),
      awardedOnLabel: t("admin.accounts.badges.modal.awarded_on_label"),
      cancel: t("admin.accounts.badges.modal.cancel"),
      confirm: t("admin.accounts.badges.modal.confirm"),
    }),
    [],
  );

  return (
    <AdminLayout activeUrl={adminAccountsPath()} account={navAccount}>
      <Head title={`${account.display_name ?? "Account"} - Admin`} />

      <AdminPageHeader
        breadcrumbs={[{ label: "Accounts", href: adminAccountsPath() }, { label: account.display_name ?? "Account" }]}
        title={account.display_name ?? "Account"}
        subtitle={<AccountMeta account={account} />}
        actions={
          <>
            {account.handle && (
              <Button size="sm" color="secondary" href={profilePath(account.handle)} target="_blank">
                View profile
              </Button>
            )}
            <Dropdown.Root>
              <Dropdown.DotsButton />
              <Dropdown.Popover className="w-min">
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === "recompute_badges") handleRecomputeBadges();
                    if (key === "delete") setDeleteOpen(true);
                  }}
                >
                  <Dropdown.Item id="recompute_badges" icon={Award02} label="Recompute badges" />
                  {!isCurrentMember && !isAnonymized && (
                    <Dropdown.Item id="delete" icon={Trash01} label="Delete account" />
                  )}
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown.Root>
          </>
        }
      />

      <DeleteEntityDialog
        entityLabel="account"
        entityName={account.display_name}
        deletionEffect={account.deletion_effect}
        commerceCount={account.commerce_count}
        isOpen={deleteOpen}
        onOpenChange={setDeleteOpen}
        onConfirm={(force) => {
          router.delete(adminAccountPath(account.id), { data: { force } });
        }}
      />

      <div className="mx-auto w-full max-w-container px-4 lg:px-8">
        <Tabs defaultSelectedKey={initialTab} onSelectionChange={onTabChange}>
          <TabList orientation="horizontal" type="button-minimal">
            <Tab id="profile" label="Profile" />
            <Tab id="selling" label="Selling" />
            <Tab id="invitation" label="Invitation" />
            <Tab id="members" label="Members" badge={String(memberships.length)} />
            <Tab id="badges" label="Badges" badge={String(badge_awards.length)} />
          </TabList>

          <TabPanel id="profile">
            <ProfileForm
              account={account}
              formData={{
                name: profileForm.data.name,
                handle: profileForm.data.handle,
                visibility: profileForm.data.visibility,
                featured_on: profileForm.data.featured_on,
                feature_url: profileForm.data.feature_url,
              }}
              errors={profileForm.errors}
              avatarPreview={avatarField.previewUrl}
              coverImagePreview={coverField.previewUrl}
              processing={profileForm.processing}
              onNameChange={handleNameChange}
              onHandleChange={handleHandleChange}
              onVisibilityChange={handleVisibilityChange}
              onFeaturedOnChange={handleFeaturedOnChange}
              onFeatureUrlChange={handleFeatureUrlChange}
              onAvatarDrop={handleAvatarDrop}
              onAvatarRemove={avatarField.onRemove}
              onCoverImageDrop={handleCoverDrop}
              onCoverImageRemove={coverField.onRemove}
              onSubmit={handleProfileSubmit}
            />
          </TabPanel>

          <TabPanel id="selling">
            <SellingForm
              formData={sellingForm.data}
              currency={account.currency}
              errors={sellingForm.errors}
              processing={sellingForm.processing}
              supportedCountries={supported_countries}
              onCountryChange={handleCountryChange}
              onSubmit={handleSellingSubmit}
            />
          </TabPanel>

          <TabPanel id="invitation">
            <div className="pt-8">
              <InvitationPanel accountId={account.id} invitation={invitation} />
            </div>
          </TabPanel>

          <TabPanel id="members">
            <div className="flex flex-col gap-6 pt-8">
              <MembersTabHeader
                memberCount={memberships.length}
                onAddMember={() => visitModal(newAdminAccountMemberPath(account.id))}
              />

              <ShowView
                account={account}
                members={memberships}
                onRemoveMember={handleRemoveMember}
                onEditMember={(member: AdminAccountsAccountMember) =>
                  visitModal(editAdminAccountMemberPath(account.id, member.id))
                }
                adminUserPath={adminUserPath}
              />
            </div>
          </TabPanel>

          <TabPanel id="badges">
            <BadgesPanel
              awards={badge_awards}
              availableBadges={available_badges}
              onAwardOpen={() => setAwardModalOpen(true)}
              onRevoke={handleRevokeBadgeRequest}
              copy={badgesPanelCopy}
            />
          </TabPanel>
        </Tabs>
      </div>

      <BadgesAwardModal
        isOpen={awardModalOpen}
        onOpenChange={setAwardModalOpen}
        availableBadges={available_badges}
        onSubmit={handleGrantBadge}
        copy={awardModalCopy}
      />

      <ConfirmationDialog
        title={t("admin.accounts.badges.revoke_dialog.title")}
        description={
          revokeAward ? t("admin.accounts.badges.revoke_dialog.description", { name: revokeAward.name }) : ""
        }
        confirmLabel={t("admin.accounts.badges.revoke_dialog.confirm")}
        cancelLabel={t("admin.accounts.badges.revoke_dialog.cancel")}
        color="error"
        isOpen={revokeAwardId !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setRevokeAwardId(null);
        }}
        onConfirm={handleRevokeBadgeConfirm}
      />
    </AdminLayout>
  );
}

AdminAccountsShowPage.layout = (page: ReactNode) => <AdminPersistentLayout>{page}</AdminPersistentLayout>;

export default AdminAccountsShowPage;
